/**
 * Copyright © 2024-2026 Entangled Harmony Labs, LLC
 * All Rights Reserved. Proprietary and Confidential.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { Play, Pause, SkipBack, SkipForward, Volume2 } from 'lucide-react';
import { TimelineEntry } from '../../types/jamTrack';

interface YouTubePlayerProps {
  videoId: string;
  timeline: TimelineEntry[];
  onTimeUpdate?: (currentTime: number, currentChord: TimelineEntry | null) => void;
  onPlayerReady?: () => void;
  className?: string;
  compact?: boolean; // New prop for compact mode
}

// Declare YouTube API types
declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  timeline,
  onTimeUpdate,
  onPlayerReady,
  className = '',
  compact = false
}) => {
  const playerRef = useRef<HTMLDivElement>(null);
  const ytPlayerRef = useRef<any>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isReady, setIsReady] = useState(false);
  const [volume, setVolume] = useState(50); // Volume 0-100
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load YouTube API
  useEffect(() => {
    if (!window.YT) {
      const tag = document.createElement('script');
      tag.src = 'https://www.youtube.com/iframe_api';
      const firstScriptTag = document.getElementsByTagName('script')[0];
      firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

      window.onYouTubeIframeAPIReady = initializePlayer;
    } else {
      initializePlayer();
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      
      // Clean up YouTube player
      if (ytPlayerRef.current) {
        try {
          ytPlayerRef.current.destroy();
        } catch (error) {
          console.warn('Error destroying YouTube player:', error);
        }
        ytPlayerRef.current = null;
      }
    };
  }, []);

  // Update video when videoId changes
  useEffect(() => {
    if (ytPlayerRef.current && videoId && isReady) {
      try {
        ytPlayerRef.current.loadVideoById(videoId);
      } catch (error) {
        console.error('Error loading video:', error);
      }
    }
  }, [videoId, isReady]);

  const initializePlayer = () => {
    if (!playerRef.current) return;

    // Dynamic sizing based on compact mode
    const playerHeight = compact ? '240' : '315';
    const playerWidth = compact ? '400' : '560'; // Bigger size in compact mode

    ytPlayerRef.current = new window.YT.Player(playerRef.current, {
      height: playerHeight,
      width: playerWidth,
      videoId: videoId || 'dQw4w9WgXcQ', // Default video if none provided
      playerVars: {
        autoplay: 0,
        controls: 0, // Hide YouTube controls since we'll add our own
        rel: 0,
        modestbranding: 1,
        fs: 1
      },
      events: {
        onReady: handlePlayerReady,
        onStateChange: handleStateChange
      }
    });
  };

  const handlePlayerReady = () => {
    try {
      if (!ytPlayerRef.current) {
        console.error('YouTube player reference is null');
        return;
      }

      setIsReady(true);
      
      // Safely get duration
      try {
        const duration = ytPlayerRef.current.getDuration();
        setDuration(duration || 0);
      } catch (error) {
        console.warn('Could not get duration, setting to 0:', error);
        setDuration(0);
      }
      
      onPlayerReady?.();
      
      // Load the correct video if different from default
      if (videoId && videoId !== 'dQw4w9WgXcQ') {
        try {
          ytPlayerRef.current.loadVideoById(videoId);
        } catch (error) {
          console.error('Error loading video in handlePlayerReady:', error);
        }
      }
      
      // Set initial volume
      try {
        ytPlayerRef.current.setVolume(volume);
      } catch (error) {
        console.warn('Could not set initial volume:', error);
      }
      
      // Start time tracking
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      intervalRef.current = setInterval(updateCurrentTime, 100);
    } catch (error) {
      console.error('Error in handlePlayerReady:', error);
    }
  };

  const handleStateChange = (event: any) => {
    const isCurrentlyPlaying = event.data === window.YT.PlayerState.PLAYING;
    setIsPlaying(isCurrentlyPlaying);
  };

  const updateCurrentTime = () => {
    try {
      if (ytPlayerRef.current && typeof ytPlayerRef.current.getCurrentTime === 'function') {
        const time = ytPlayerRef.current.getCurrentTime();
        setCurrentTime(time);

        // Find current chord based on timeline
        const currentChord = timeline.find(
          entry => time >= entry.startTime && time < entry.endTime
        ) || null;

        onTimeUpdate?.(time, currentChord);
      }
    } catch (error) {
      console.warn('Error updating current time:', error);
      // Don't spam console with errors, just continue
    }
  };

  const togglePlayPause = () => {
    if (!ytPlayerRef.current) return;

    if (isPlaying) {
      ytPlayerRef.current.pauseVideo();
    } else {
      ytPlayerRef.current.playVideo();
    }
  };

  const skipToTime = (seconds: number) => {
    if (!ytPlayerRef.current) return;
    ytPlayerRef.current.seekTo(Math.max(0, Math.min(seconds, duration)), true);
  };

  const skipToNextChord = () => {
    const nextChord = timeline.find(entry => entry.startTime > currentTime);
    if (nextChord) {
      skipToTime(nextChord.startTime);
    }
  };

  const skipToPrevChord = () => {
    // Find the previous chord or beginning of current chord
    const currentChord = timeline.find(
      entry => currentTime >= entry.startTime && currentTime < entry.endTime
    );
    
    if (currentChord && currentTime > currentChord.startTime + 2) {
      // If we're more than 2 seconds into current chord, go to beginning of current chord
      skipToTime(currentChord.startTime);
    } else {
      // Otherwise, go to previous chord
      const prevChord = [...timeline]
        .reverse()
        .find(entry => entry.startTime < currentTime - 1);
      if (prevChord) {
        skipToTime(prevChord.startTime);
      } else {
        skipToTime(0);
      }
    }
  };

  const handleVolumeChange = (newVolume: number) => {
    setVolume(newVolume);
    if (ytPlayerRef.current && isReady) {
      try {
        ytPlayerRef.current.setVolume(newVolume);
      } catch (error) {
        console.warn('Could not set volume:', error);
      }
    }
  };

  const formatTime = (time: number): string => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  return (
    <Card className={className}>
      <CardContent className={compact ? "p-2" : "p-4"}>
        <div className={compact ? "space-y-2" : "space-y-4"}>
          {/* YouTube Player Container */}
          <div className={`relative bg-black rounded-lg overflow-hidden ${compact ? 'w-96 h-48' : 'aspect-video'}`}>
            <div ref={playerRef} className="w-full h-full" />
            
            {!isReady && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/80 text-white">
                <div className="animate-spin h-8 w-8 border-2 border-white border-t-transparent rounded-full"></div>
              </div>
            )}
          </div>

          {/* Custom Controls */}
          <div className={compact ? "space-y-1" : "space-y-3"}>
            {/* Progress Bar */}
            {!compact && (
              <div className="space-y-2">
                <div className="w-full bg-muted rounded-full h-2 cursor-pointer"
                     onClick={(e) => {
                       const rect = e.currentTarget.getBoundingClientRect();
                       const x = e.clientX - rect.left;
                       const percentage = x / rect.width;
                       skipToTime(percentage * duration);
                     }}>
                  <div 
                    className="bg-primary h-2 rounded-full transition-all"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
                
                <div className="flex justify-between text-xs text-muted-foreground">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>
            )}

            {/* Control Buttons */}
            <div className={`flex items-center ${compact ? 'justify-start gap-1' : 'justify-center gap-2'}`}>
              <Button
                variant="outline"
                size={compact ? "sm" : "sm"}
                onClick={skipToPrevChord}
                disabled={!isReady}
              >
                <SkipBack className={compact ? "h-3 w-3" : "h-4 w-4"} />
              </Button>
              
              <Button
                variant="outline"
                size={compact ? "sm" : "sm"}
                onClick={togglePlayPause}
                disabled={!isReady}
              >
                {isPlaying ? <Pause className={compact ? "h-3 w-3" : "h-4 w-4"} /> : <Play className={compact ? "h-3 w-3" : "h-4 w-4"} />}
              </Button>
              
              <Button
                variant="outline"
                size={compact ? "sm" : "sm"}
                onClick={skipToNextChord}
                disabled={!isReady}
              >
                <SkipForward className={compact ? "h-3 w-3" : "h-4 w-4"} />
              </Button>
              
              {/* Volume control - always show but compact in compact mode */}
              <div className={`flex items-center gap-2 ${compact ? 'ml-2' : 'ml-4'}`}>
                <Volume2 className={compact ? "h-3 w-3 text-muted-foreground" : "h-4 w-4 text-muted-foreground"} />
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={volume}
                  onChange={(e) => handleVolumeChange(parseInt(e.target.value))}
                  className={`bg-muted rounded-lg appearance-none cursor-pointer slider ${compact ? 'w-12 h-1' : 'w-16 h-1'}`}
                  disabled={!isReady}
                />
                <span className={`text-muted-foreground min-w-[3ch] ${compact ? 'text-xs' : 'text-xs'}`}>{volume}%</span>
              </div>

              {compact && (
                <div className="flex items-center gap-1 ml-2">
                  <span className="text-xs text-muted-foreground">{formatTime(currentTime)}</span>
                  <span className="text-xs text-muted-foreground">/</span>
                  <span className="text-xs text-muted-foreground">{formatTime(duration)}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default YouTubePlayer;